import {
    Component, OnInit, ViewChild, AfterViewInit, ChangeDetectorRef,
    ViewChildren, QueryList, DestroyRef, inject
} from '@angular/core';
import {BehaviorSubject, finalize, Observable, of} from 'rxjs';
import {map, take, switchMap} from 'rxjs/operators';
import * as R from 'ramda';
import * as RA from 'ramda-adjunct';
import {ItemsFormComponent} from '../items-form/items-form.component';
import {AttributeGroup} from '@emm/core/models/attribute-group.model';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Item} from '@emm/core/models/item.model';
import {ContextMenuAction} from '@emm/core/models/context-menu-action.model';
import {AuthFacadeService} from '@emm/auth/services/auth-facade.service';
import {DialogService} from '@emm/shared/services/dialog.service';
import {ItemsFacadeService} from '@emm/management/services/items-facade.service';
import {NotificationService} from '@emm/shared/services/notification.service';
import {SearchFilter} from '@emm/core/models/search-filter';
import {itemAttributeFilterFieldMap as fieldMap} from '@emm/shared/formly/maps/item-attribute-filter.config';
import {UserService} from '@emm/core/services/user.service';
import {API_ERRORS} from '@emm/core/util/api-constants';


@Component({
    selector: 'emm-items',
    styleUrls: ['./items.component.scss'],
    templateUrl: './items.component.html',
    standalone: false
})
export class ItemsComponent implements OnInit, AfterViewInit {

    @ViewChildren(ItemsFormComponent) public components: QueryList<ItemsFormComponent>;
    @ViewChild(ItemsFormComponent) itemsForm: ItemsFormComponent;

    private readonly destroyRef: DestroyRef = inject(DestroyRef);
    anyLoading$ = this.itemsFacade.anyLoading$;
    centerLoading$ = this.itemsFacade.centerLoading$;
    isReadOnly$ = this.authFacade.isReadOnly$;
    isReadOnly = this.userService.hasLoginRole();
    isSuperAdmin = this.userService.hasSuperAdminRole();
    isAdmin = this.userService.hasAdminRole();
    isAuthor = this.userService.hasAuthorRole();
    isMMSAuthor = this.userService.isMmsAuthorOnly();
    itemDetail$ = this.itemsFacade.itemDetail$;
    items$ = new BehaviorSubject<any>(undefined);
    prevItem: Item;
    rightLoading$ = this.itemsFacade.rightLoading$;
    itemsCache = [];
    itemTypes$ = this.itemsFacade.itemTypes$
    itemTypes = new BehaviorSubject<any>(null);
    previousAttributeGroups$ = new BehaviorSubject<AttributeGroup[]>([]);

    fields = [fieldMap['name'](), fieldMap['tag'](), fieldMap['POS_ID'](), fieldMap['RMI'](),];

    itemsContextMenuActions = [
        {
            enabled: (item) => !item.locked && this.isSuperAdmin,
            click: (item) => this.onDeleteItem(item),
            visible: this.isSuperAdmin ? R.T : R.F,
            icon: R.always('delete'),
            text: R.always('Delete')
        },
        {
            enabled: (item) => !item.locked,
            click: (item) => this.onToggleEnabled(item),
            visible: this.isSuperAdmin ? R.T : R.F,
            icon: (item) => (!item.disabled ? 'visibility_off' : 'visibility'),
            text: (item) => (!item.disabled ? 'Disable' : 'Enable')
        },
    ] as ContextMenuAction<Item>[];

    constructor(
        private authFacade: AuthFacadeService,
        private dialogService: DialogService,
        private itemsFacade: ItemsFacadeService,
        private notificationService: NotificationService,
        private changeDetector: ChangeDetectorRef,
        private userService: UserService,
    ) {
    }


    ngOnInit(): void {
        this.itemsFacade.init();
        this.items$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
        this.itemTypes$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(itemTypes => this.itemTypes.next(itemTypes));
        this.anyLoading$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
        this.getItems();
        this.itemDetail$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }


    ngAfterViewInit() {
        this.components.changes.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((comps: QueryList<ItemsFormComponent>) => {
            this.itemsForm = comps.first;
            this.changeDetector.detectChanges();
        });
    }

    getItems(): void {
        this.itemsFacade.startProgressBar();
        this.itemsFacade.itemsAsGenericItems$
            .pipe(
                finalize(() => this.itemsFacade.stopProgressBar()),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(result => {
            this.itemsCache = result;
            this.items$.next(result);
        });
    }


    onFilter(searchFilter: SearchFilter): void {
        this.filterNodes(searchFilter.value as string, searchFilter.key);
    }


    filterNodes(value: string, selected: string): void {
        this.items$.next(this.itemsCache);
        this.items$.pipe(
            take(1),
            map(list => {
                let filteredNodes;
                try {
                    switch (selected) {
                        case 'POS_ID':
                            filteredNodes = list.filter(nd => nd['source'].attributes[1]['value'] === value.toString());
                            break;
                        case 'RMI':
                            filteredNodes = list.filter(nd => nd['source'].attributes[2]['value'] === value.toString());
                            break;
                        case 'NAME':
                            filteredNodes = list.filter(nd => nd['name'].toLowerCase().includes(value.toLowerCase()));
                            break;
                        default:
                            filteredNodes = list.filter(nd => (
                                nd.tag.toLowerCase().includes(value.toLowerCase()) || nd.tag.toLowerCase() ===
                                value.toString().toLowerCase()));
                            break;
                    }
                    this.items$.next(filteredNodes);
                } catch (e) {}
            }),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe();
    }


    onSelectItem(selected: Item): void {
        if (RA.isNotNil(selected)) {
            this.checkIfItemHasChanges(this.prevItem)
                .pipe(take(1), takeUntilDestroyed(this.destroyRef))
                .subscribe(() => {
                    this.prevItem = selected;
                    this.itemsFacade.selectItem(selected);
                    this.itemsFacade.getItemLocale(selected.tag, 'US', '')
                        .pipe(
                            takeUntilDestroyed(this.destroyRef)
                        )
                        .subscribe(result =>
                        this.itemsFacade.itemLocaleDetail$.next(result))
                });
        } else {
            this.prevItem = null;
        }
    }


    onToggleEnabled(item: Item): void {
        this.itemsFacade.toggleEnabled(item)
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (result) => {
                    this.notificationService.success(`Successfully ${item.disabled ? 'enabled' : 'disabled'} ${item.name}`);
                    this.getItems();
                },
                error: (error) => {
                    this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
                }
            });
    }


    onDeleteItem(item: Item): void {
        const dialogRef = this.dialogService.openConfirmDialog('Are you sure you want to delete',
            item.name, null);
        try {
            dialogRef.afterClosed().pipe(
                switchMap(result => result ? this.itemsFacade.api.deleteItem(item.tag) : of(false)),
                takeUntilDestroyed(this.destroyRef)
            ).subscribe({
                next: (response) => {
                    if (response === false) {
                        this.notificationService.error(`Delete was cancelled`);
                    } else {
                        this.notificationService.success(`Successfully deleted ${item.name}`);
                        this.getItems();
                    }
                },
                error: (error) => {
                    this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
                }
            });
        } catch (e) {
            this.notificationService.error(`Failed to delete ${item.name}`);
        }
    }


    checkIfItemHasChanges(prevItem: Item): Observable<boolean> {
        return this.isReadOnly$.pipe(
            take(1),
            switchMap((isReadOnly) => {
                if (this.hasChanges() && !isReadOnly && RA.isNotNil(prevItem)) {
                    return this.dialogService
                        .openSaveChangesDialog(prevItem.name)
                        .afterClosed()
                        .pipe(
                            switchMap(res => {
                                if (res) {
                                    this.itemsForm.submit(prevItem.tag);
                                    return this.itemsFacade.updateItem$ as Observable<boolean>;
                                }
                                this.itemsForm.dynamicForms.forEach(df => {
                                    if (df.form.dirty) {
                                        this.itemsForm.cancel(df.attributeGroupId);
                                    }
                                })
                                return of(true);
                            })
                        );
                }
                return of(true);
            }),
            takeUntilDestroyed(this.destroyRef)
        );
    }


    hasChanges(): boolean {
        return this.itemsForm && this.itemsForm.hasChanges() === true;
    }

    onAttributeGroupSelected($event) {
        this.previousAttributeGroups$.next($event);
    }
}
